import { useState, useEffect } from 'react';
import { MessageCircle, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useChat } from '@/hooks/useChat';
import { ChatConversationList } from './ChatConversationList';
import { ChatPanel } from './ChatPanel';
import { NewConversationDialog } from './NewConversationDialog';
import { motion, AnimatePresence } from 'framer-motion';

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [prevUnread, setPrevUnread] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const {
    conversations,
    messages,
    conversationsLoading,
    messagesLoading,
    activeConversationId,
    setActiveConversationId,
    createConversation,
    sendMessage,
    markAsRead,
    closeConversation,
    reopenConversation,
    totalUnread,
    isAdmin
  } = useChat();

  const activeConversation = conversations.find(c => c.id === activeConversationId);

  // Mark as read when viewing conversation
  useEffect(() => {
    if (activeConversationId && isOpen) {
      markAsRead.mutate(activeConversationId);
    }
  }, [activeConversationId, isOpen, messages.length]);

  // Trigger animation when new messages arrive
  useEffect(() => {
    if (totalUnread > prevUnread && !isOpen) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 600);
      return () => clearTimeout(timer);
    }
    setPrevUnread(totalUnread);
  }, [totalUnread, prevUnread, isOpen]);

  const handleCreateConversation = (subject: string, message: string) => {
    createConversation.mutate(
      { subject, initialMessage: message },
      {
        onSuccess: () => setShowNewDialog(false)
      }
    );
  };

  const handleSendMessage = (content: string) => {
    if (!activeConversationId) return;
    sendMessage.mutate({ conversationId: activeConversationId, content });
  };

  const handleBack = () => {
    setActiveConversationId(null);
  };

  const handleClose = () => {
    if (activeConversationId) {
      closeConversation.mutate(activeConversationId);
    }
  };

  const handleReopen = () => {
    if (activeConversationId) {
      reopenConversation.mutate(activeConversationId);
    }
  };

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          size="icon"
          className={cn(
            'h-14 w-14 rounded-full shadow-lg transition-all',
            isOpen && 'rotate-90'
          )}
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <MessageCircle className="h-6 w-6" />
          )}
        </Button>
        {!isOpen && totalUnread > 0 && (
          <motion.div
            key={totalUnread}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ 
              scale: isAnimating ? [1, 1.3, 1] : 1,
              opacity: 1,
            }}
            transition={{ 
              duration: 0.4,
              ease: "easeOut",
              scale: {
                duration: 0.5,
                times: [0, 0.5, 1],
              }
            }}
            className="absolute -top-1 -right-1"
          >
            <Badge 
              className={cn(
                "h-6 min-w-6 flex items-center justify-center",
                isAnimating && "animate-pulse"
              )}
              variant="destructive"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </Badge>
          </motion.div>
        )}
      </div>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[380px] h-[500px] bg-background border rounded-xl shadow-xl overflow-hidden flex flex-col"
          >
            {activeConversationId && activeConversation ? (
              <ChatPanel
                conversationSubject={activeConversation.subject}
                conversationStatus={activeConversation.status as 'open' | 'closed'}
                messages={messages}
                isLoading={messagesLoading}
                onSendMessage={handleSendMessage}
                onBack={handleBack}
                onClose={isAdmin ? handleClose : undefined}
                onReopen={isAdmin ? handleReopen : undefined}
                isAdmin={isAdmin}
                userName={activeConversation.user?.name}
              />
            ) : (
              <>
                {/* List header */}
                <div className="flex items-center justify-between p-4 border-b bg-muted/30">
                  <h2 className="font-semibold">
                    {isAdmin ? 'Support Chat' : 'Mes conversations'}
                  </h2>
                  {!isAdmin && (
                    <Button 
                      size="sm" 
                      onClick={() => setShowNewDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Nouveau
                    </Button>
                  )}
                </div>

                {/* Conversation list */}
                <ChatConversationList
                  conversations={conversations}
                  activeId={activeConversationId}
                  onSelect={setActiveConversationId}
                  isAdmin={isAdmin}
                />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* New conversation dialog */}
      <NewConversationDialog
        open={showNewDialog}
        onOpenChange={setShowNewDialog}
        onSubmit={handleCreateConversation}
        isLoading={createConversation.isPending}
      />
    </>
  );
}
